import type { ReactElement } from 'react';
import { FileImage, Maximize2, Minus, Plus, RotateCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { AutosaveIndicator, type AutosaveStatus } from './AutosaveIndicator';
import { SubmissionStateBadge, type SubmissionTrustState } from '../../trust/SubmissionStateBadge';

export type SubmissionKind = 'paper' | 'digital' | 'none';

type ViewerToolbarProps = {
  studentName: string;
  assignmentTitle?: string;
  kind: SubmissionKind;
  zoomPercent: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRotate: () => void;
  fitWidth: boolean;
  onToggleFitWidth: () => void;
  annotateEnabled?: boolean;
  annotateOn?: boolean;
  onToggleAnnotate?: () => void;
  autosaveStatus: AutosaveStatus;
  submissionTrustState?: SubmissionTrustState | null;
  submissionSynced?: boolean;
  className?: string;
};

export function ViewerToolbar({
  studentName,
  assignmentTitle,
  kind,
  zoomPercent,
  onZoomIn,
  onZoomOut,
  onRotate,
  fitWidth,
  onToggleFitWidth,
  annotateEnabled = false,
  annotateOn = false,
  onToggleAnnotate,
  autosaveStatus,
  submissionTrustState = null,
  submissionSynced = false,
  className,
}: ViewerToolbarProps): ReactElement {
  const { t } = useTranslation();

  return (
    <header
      className={cn(
        'flex h-14 shrink-0 flex-wrap items-center gap-2 border-b border-slate-200/80 bg-white/95 px-4 py-2 dark:border-slate-700/70 dark:bg-slate-900/80',
        className
      )}
    >
      <div className="min-w-0 flex-1 basis-[200px]">
        <div className="truncate text-dg-body font-semibold text-slate-900 dark:text-white">
          {studentName}
        </div>
        {assignmentTitle ? (
          <div className="truncate text-dg-secondary text-slate-600 dark:text-slate-400">
            {assignmentTitle}
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1 rounded-dg-control border border-slate-200/80 bg-slate-50 px-2 py-1 text-dg-label font-medium text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
          title={t('audit.gradeStudio.submissionType')}
        >
          <FileImage className="h-3.5 w-3.5 shrink-0 text-dg-accent-grade" aria-hidden />
          {kind === 'paper'
            ? t('audit.gradeStudio.typePaper')
            : kind === 'digital'
              ? t('audit.gradeStudio.typeDigital')
              : t('audit.gradeStudio.typeNone')}
        </span>
        {submissionTrustState ? (
          <SubmissionStateBadge state={submissionTrustState} synced={submissionSynced} />
        ) : null}
        <AutosaveIndicator status={autosaveStatus} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center rounded-dg-control border border-slate-200/80 bg-white dark:border-slate-600 dark:bg-slate-950">
          <button
            type="button"
            onClick={onZoomOut}
            className="flex h-9 w-9 items-center justify-center rounded-l-dg-control text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label={t('audit.gradeStudio.zoomOut')}
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="min-w-[3rem] text-center text-dg-label font-medium tabular-nums text-slate-700 dark:text-slate-200">
            {zoomPercent}%
          </span>
          <button
            type="button"
            onClick={onZoomIn}
            className="flex h-9 w-9 items-center justify-center rounded-r-dg-control text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label={t('audit.gradeStudio.zoomIn')}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={onRotate}
          className="flex h-9 items-center gap-1.5 rounded-dg-control border border-slate-200/80 bg-white px-3 text-dg-label font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <RotateCw className="h-4 w-4" aria-hidden />
          {t('audit.gradeStudio.rotate')}
        </button>
        <button
          type="button"
          onClick={onToggleFitWidth}
          className={cn(
            'flex h-9 items-center gap-1.5 rounded-dg-control border px-3 text-dg-label font-medium transition-colors',
            fitWidth
              ? 'border-dg-accent-grade/40 bg-dg-accent-gradeLight text-amber-950 dark:border-amber-600/40 dark:bg-amber-950/30 dark:text-amber-100'
              : 'border-slate-200/80 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800'
          )}
        >
          <Maximize2 className="h-4 w-4" aria-hidden />
          {t('audit.gradeStudio.fitWidth')}
        </button>
        {annotateEnabled && onToggleAnnotate ? (
          <button
            type="button"
            onClick={onToggleAnnotate}
            className={cn(
              'h-9 rounded-dg-control border px-3 text-dg-label font-medium',
              annotateOn
                ? 'border-dg-accent-grade/40 bg-dg-accent-gradeLight text-amber-950 dark:border-amber-600/40 dark:bg-amber-950/30 dark:text-amber-100'
                : 'border-slate-200/80 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-200'
            )}
          >
            {t('audit.gradeStudio.annotate')}
          </button>
        ) : null}
      </div>
    </header>
  );
}
